import React, { useEffect, useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList
} from "recharts";
import {
  Spinner,
  Card,
  OverlayTrigger,
  Tooltip,
  Modal,
  Button,
} from "react-bootstrap";
import { FiInfo, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const DashboardOrdiniOggi = () => {
  const [dataOrdini, setDataOrdini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  
  // STATO PER LO SWITCH VISIVO: 0 = Vista Torta, 1 = Vista Barre
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const ordini = data?.ORDINI_OGGI?.flat() || [];
        setDataOrdini(ordini);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch dati ordini:", err);
        setLoading(false);
      });
  }, []);

  // Helper formattazione Euro
  const formatEuro = (value) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  // ELABORAZIONE GLOBALE DEI DATI
  const metricheOrdini = useMemo(() => {
    let pezziUrgenti = 0;
    let pezziStandard = 0;
    let importoUrgenti = 0;
    let importoStandard = 0;

    dataOrdini.forEach((item) => {
      const valore = parseFloat(item.importo_doc) || 0;
      if (item.tipo_doc === "OF4G") {
        pezziUrgenti++;
        importoUrgenti += valore;
      } else {
        pezziStandard++;
        importoStandard += valore;
      }
    });

    const totalePezzi = pezziUrgenti + pezziStandard;

    return {
      totalePezzi,
      legendaData: [
        { name: "Urgenti (OF4G)", value: pezziUrgenti, importo: importoUrgenti, color: "#b81f25" },
        { name: "Standard / Altri", value: pezziStandard, importo: importoStandard, color: "#adb5bd" },
      ],
      ciambellaData: [
        { name: "Urgenti (OF4G)", value: pezziUrgenti, color: "#b81f25" },
        { name: "Standard / Altri", value: pezziStandard, color: "#e9ecef" },
      ],
      barreData: [
        { name: "URGENTI", totale: importoUrgenti, color: "#b81f25" },
        { name: "STANDARD", totale: importoStandard, color: "#adb5bd" }
      ]
    };
  }, [dataOrdini]);

  // Controlli switch grafico
  const toggleSlide = (e) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
  };

  const renderWidgetTooltip = (props) => (
    <Tooltip id="widget-info-tooltip" {...props}>
      <div style={{ textAlign: "left", fontSize: "0.75rem", padding: "4px" }}>
        {currentSlide === 0 ? (
          <>Questo grafico mostra la proporzione numerica degli ordini odierni, isolando i documenti urgenti <strong>(OF4G)</strong>.</>
        ) : (
          <>Questo grafico mostra il volume finanziario complessivo espresso in Euro (€).</>
        )}
      </div>
    </Tooltip>
  );

  // LEGENDA UNIFICATA E IMMOBILE (Sempre visibile sotto entrambi i grafici)
  const RenderLegendaFissa = ({ isFullscreen = false }) => (
    <div className={isFullscreen ? "d-flex justify-content-center gap-4 w-100" : "d-flex justify-content-between gap-2 w-100"}>
      {metricheOrdini.legendaData.map((item, i) => (
        <div
          key={i}
          className="d-flex flex-column align-items-center flex-fill p-2 rounded-3"
          style={{ background: "#f8f9fa", maxWidth: isFullscreen ? "340px" : "100%" }}
        >
          <div className="d-flex align-items-center mb-1">
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: item.color, marginRight: "6px" }}></div>
            <span style={{ fontSize: isFullscreen ? "0.75rem" : "0.65rem", fontWeight: "700", color: "#6c757d" }}>{item.name}</span>
          </div>
          <div className="d-flex flex-column align-items-center">
            <span className="fw-bold" style={{ fontSize: isFullscreen ? "1.2rem" : "0.95rem", color: "#212529" }}>
              {item.value} <span className="fw-normal text-muted" style={{ fontSize: "0.7rem" }}>ordini</span>
            </span>
            <span className="text-muted fw-semibold" style={{ fontSize: isFullscreen ? "0.85rem" : "0.7rem" }}>
              {formatEuro(item.importo)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  // SOLO I GRAFICI CAMBIANO IN BASE A CURRENT_SLIDE
  const renderOnlyChartContent = (isFullscreen = false) => {
    if (currentSlide === 0) {
      // GRAFICO 1: TORTA / CIAMBELLA
      return (
        <div style={{ position: "relative", height: "100%", width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={metricheOrdini.ciambellaData}
                cx="50%"
                cy="50%"
                innerRadius={isFullscreen ? 110 : 55}
                outerRadius={isFullscreen ? 150 : 80}
                paddingAngle={6}
                dataKey="value"
                stroke="none"
                cornerRadius={isFullscreen ? 8 : 5}
              >
                {metricheOrdini.ciambellaData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} style={{ outline: "none" }} />
                ))}
              </Pie>
              <ChartTooltip
                cursor={{ fill: "transparent" }}
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", fontSize: "0.8rem" }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
            <span style={{ fontSize: isFullscreen ? "0.85rem" : "0.6rem", color: "#6c757d", fontWeight: "600", textTransform: "uppercase" }}>
              Totale Ordini
            </span>
            <h2 className="m-0 fw-bold" style={{ color: "#212529", lineHeight: "1", fontSize: isFullscreen ? "3rem" : "1.5rem" }}>
              {metricheOrdini.totalePezzi}
            </h2>
          </div>
        </div>
      );
    } else {
      // GRAFICO 2: BARRE ORIZZONTALI
      return (
        <div style={{ width: "100%", height: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={metricheOrdini.barreData} margin={{ top: 20, right: isFullscreen ? 110 : 75, left: 5, bottom: 20 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" stroke="#6c757d" fontSize={10} fontWeight={700} tickLine={false} axisLine={false} />
              <ChartTooltip
                cursor={{ fill: "#f8f9fa" }}
                contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", fontSize: "0.8rem" }}
                formatter={(value) => [formatEuro(value), "Totale"]}
              />
              <Bar dataKey="totale" radius={[0, 10, 10, 0]} barSize={isFullscreen ? 45 : 28}>
                {metricheOrdini.barreData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="totale"
                  position="right"
                  formatter={formatEuro}
                  style={{ fontSize: isFullscreen ? "0.9rem" : "0.7rem", fontWeight: "bold", fill: "#333" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm d-flex justify-content-center align-items-center" style={{ height: "420px", borderRadius: "15px" }}>
        <Spinner animation="grow" variant="danger" />
      </Card>
    );
  }

  return (
    <Card className="border-0 card overflow-hidden" style={{ borderRadius: "10px", height: "420px", background: "#fff" }}>
      {/* HEADER CON SELETTORE GRAFICO */}
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white" style={{ zIndex: 110 }}>
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span style={{ fontWeight: "800", fontSize: "0.75rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
            PANORAMICA ORDINI OGGI
          </span>
          <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={renderWidgetTooltip}>
            <span onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>

        {/* FRECCE DI CAMBIO GRAFICO */}
        <div className="d-flex align-items-center gap-1">
          <Button variant="light" size="sm" className="p-1 d-flex align-items-center justify-content-center rounded-circle border border-secondary border-opacity-10" style={{ width: "26px", height: "26px" }} onClick={toggleSlide}>
            <FiChevronLeft size={16} color="#495057" />
          </Button>
          
          <Button variant="light" size="sm" className="p-1 d-flex align-items-center justify-content-center rounded-circle border border-secondary border-opacity-10" style={{ width: "26px", height: "26px" }} onClick={toggleSlide}>
            <FiChevronRight size={16} color="#495057" />
          </Button>
        </div>
      </div>

      {/* CORPO CARD PRINCIPALE */}
      <Card.Body className="d-flex flex-column p-3 justify-content-between overflow-hidden">
        {/* Zona Grafico Dinamica */}
        <div className="flex-grow-1" style={{ height: "190px" }}>
          {renderOnlyChartContent(false)}
        </div>
        
        {/* Zona Legenda Fissa */}
        <div className="w-100 px-2 mt-2">
          <RenderLegendaFissa isFullscreen={false} />
        </div>
      </Card.Body>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                PANORAMICA ORDINI OGGI (SCHERMO INTERO)
              </span>
              <OverlayTrigger placement="bottom" overlay={renderWidgetTooltip}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                  <FiInfo size={16} color="#adb5bd" />
                </span>
              </OverlayTrigger>
            </div>

            {/* Controlli Slide in Modale */}
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2 bg-light px-3 py-1 rounded-pill">
                <Button variant="link" size="sm" className="p-0 text-dark d-flex align-items-center" onClick={toggleSlide}>
                  <FiChevronLeft size={20} />
                </Button>
                <span style={{ fontSize: "0.8rem", fontWeight: "700", minWidth: "90px", textAlign: "center" }}>
                  {currentSlide === 0 ? "Grafico Torta" : "Grafico Barre"}
                </span>
                <Button variant="link" size="sm" className="p-0 text-dark d-flex align-items-center" onClick={toggleSlide}>
                  <FiChevronRight size={20} />
                </Button>
              </div>

              <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
                <FiX size={16} /> Chiudi
              </Button>
            </div>
          </div>

          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center">
            <div className="bg-white shadow-sm rounded-4 border p-5 w-100 d-flex flex-column align-items-center justify-content-between" style={{ maxWidth: "1000px", height: "80vh" }}>
              {/* Grafico Dinamico in Modale */}
              <div className="w-100 flex-grow-1" style={{ height: "380px" }}>
                {renderOnlyChartContent(true)}
              </div>
              {/* Legenda Fissa sotto anche nel modale */}
              <div className="w-100 mt-4 pt-3 border-top">
                <RenderLegendaFissa isFullscreen={true} />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default DashboardOrdiniOggi;